//
// apps/vendor/src/pages/OrderInbox.tsx
//
// Phase 6 — Vendor unified inbox.
//
// Shows BOTH modes of vendor-surfaced work in one list:
//   - Mode A (federation): orders federated IN from partner tenants that the
//     vendor tenant is handling (order_type = 'federated_incoming').
//   - Mode B (share links): orders the current viewer has issued a share
//     link for — surfaced so the viewer can manage (revoke) their own links.
//
// NB (2026-07): this view is powered by the `mySharedOrders` resolver, which
// is a DIFFERENT query from the primary Inbox (`orders(FEDERATED_INCOMING)`).
// When the two disagree on the federated count it's a backend reconciliation
// gap, not a display bug — the primary Inbox tab is the source of truth for
// actionable work.

import { Button } from "@clearpath/design-system/ui/button";
import { Link } from "react-router-dom";
import { Badge } from '@clearpath/design-system/ui/badge';
import { useMySharedOrdersQuery } from "../__generated__/graphql";
import { humanizeEnum } from "../lib/federation-format";

export function OrderInbox() {
  const { data, loading, error, refetch } = useMySharedOrdersQuery({
    fetchPolicy: "cache-and-network",
  });

  if (loading && !data) {
    return (
      <section aria-label="Vendor order inbox" className="inbox-page">
        <p className="loading-row">Loading inbox…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section aria-label="Vendor order inbox" className="inbox-page">
        <div className="notice notice-danger" role="alert">
          <p>Couldn’t load your inbox.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </section>
    );
  }

  const rows = data?.mySharedOrders ?? [];
  const federated = rows.filter((r) => r.source === "federation");
  const shared = rows.filter((r) => r.source === "share_link");

  return (
    <section aria-label="Vendor order inbox" className="inbox-page">
      <header className="page-head">
        <div>
          <h1>Unified</h1>
          <p className="page-sub">
            {federated.length} federated · {shared.length} shared link
            {shared.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="page-actions">
          <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
            Refresh
          </Button>
        </div>
      </header>

      <section aria-label="Federated orders" className="inbox-section">
        <h2>Federated (Mode A)</h2>
        {federated.length === 0 ? (
          <div className="empty-state compact">
            <div className="empty-icon" aria-hidden="true">
              📥
            </div>
            <h3>No federated orders here</h3>
            <p>
              Actionable federated work lives on the <Link to="/">Inbox</Link>{" "}
              tab. This unified view aggregates the <code>mySharedOrders</code>{" "}
              feed.
            </p>
          </div>
        ) : (
          <div className="table-card">
            <table className="inbox">
              <thead>
                <tr>
                  <th scope="col">Order ref</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Due</th>
                  <th scope="col">Created</th>
                </tr>
              </thead>
              <tbody>
                {federated.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link className="order-ref" to={`/orders/${row.id}`}>
                        {row.barcodeValue ?? row.id}
                      </Link>
                    </td>
                    <td>{humanizeEnum(row.orderType)}</td>
                    <td>
                      <Badge variant="neutral">{humanizeEnum(row.status)}</Badge>
                    </td>
                    <td>
                      {row.estimatedCompletionAt ? (
                        new Date(row.estimatedCompletionAt).toLocaleDateString()
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                    </td>
                    <td>
                      <time dateTime={row.createdAt}>
                        {new Date(row.createdAt).toLocaleDateString()}
                      </time>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-label="Share links" className="inbox-section">
        <h2>Share links (Mode B)</h2>
        {shared.length === 0 ? (
          <div className="empty-state compact">
            <div className="empty-icon" aria-hidden="true">
              🔗
            </div>
            <h3>No orders shared via link</h3>
            <p>
              When you issue a share link for an order, it appears here so you
              can track or revoke it.
            </p>
          </div>
        ) : (
          <div className="table-card">
            <table className="inbox">
              <thead>
                <tr>
                  <th scope="col">Order ref</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created</th>
                </tr>
              </thead>
              <tbody>
                {shared.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link className="order-ref" to={`/shared/${row.id}`}>
                        {row.barcodeValue ?? row.id}
                      </Link>
                    </td>
                    <td>{humanizeEnum(row.orderType)}</td>
                    <td>
                      <Badge variant="neutral">{humanizeEnum(row.status)}</Badge>
                    </td>
                    <td>
                      <time dateTime={row.createdAt}>
                        {new Date(row.createdAt).toLocaleDateString()}
                      </time>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
